import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';


const REACTIONS = [
{ id: 'coin', icon: 'Coins', label: 'Gold Coin', color: 'var(--color-accent)' },
{ id: 'insight', icon: 'Lightbulb', label: 'Insightful', color: '#F59E0B' },
{ id: 'crown', icon: 'Crown', label: 'Celebrate', color: 'var(--color-accent)' },
{ id: 'support', icon: 'Heart', label: 'Support', color: '#EC4899' }];


export default function PostCard({ post }) {
  const [reactions, setReactions] = useState(post?.reactions || {});
  const [userReaction, setUserReaction] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');

  const handleReaction = (reactionId) => {
    setReactions((prev) => {
      const updated = { ...prev };
      if (userReaction === reactionId) {
        updated[reactionId] = Math.max(0, (updated?.[reactionId] || 1) - 1);
        setUserReaction(null);
      } else {
        if (userReaction) updated[userReaction] = Math.max(0, (updated?.[userReaction] || 1) - 1);
        updated[reactionId] = (updated?.[reactionId] || 0) + 1;
        setUserReaction(reactionId);
      }
      return updated;
    });
    setShowReactionPicker(false);
  };

  const totalReactions = Object.values(reactions)?.reduce((a, b) => a + b, 0);

  const typeColors = {
    question: 'bg-blue-100 text-blue-700',
    milestone: 'bg-green-100 text-green-700',
    spotlight: 'bg-purple-100 text-purple-700',
    insight: 'bg-amber-100 text-amber-700',
    poll: 'bg-pink-100 text-pink-700',
    text: 'bg-gray-100 text-gray-600'
  };

  const typeLabels = {
    question: 'Investment Question',
    milestone: 'Milestone',
    spotlight: 'Business Spotlight',
    insight: 'Market Insight',
    poll: 'Poll',
    text: 'Post'
  };

  return (
    <article className="bg-white rounded-xl border p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 md:w-11 md:h-11 rounded-full overflow-hidden flex-shrink-0">
          <Image src={post?.avatar} alt={post?.avatarAlt} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-caption font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>{post?.author}</span>
            {post?.badge &&
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-caption font-medium" style={{ background: 'rgba(133,113,77,0.12)', color: 'var(--color-accent)' }}>
                <Icon name="Crown" size={10} color="var(--color-accent)" />
                {post?.badge}
              </span>
            }
            <span className={`px-2 py-0.5 rounded-full text-xs font-caption font-medium ${typeColors?.[post?.type] || typeColors?.text}`}>
              {typeLabels?.[post?.type] || 'Post'}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{post?.role}</span>
            <span className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>·</span>
            <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{post?.timeAgo}</span>
          </div>
        </div>
        <button className="p-1.5 rounded-md hover:bg-gray-100 transition-colors flex-shrink-0">
          <Icon name="MoreHorizontal" size={16} color="var(--color-muted-foreground)" />
        </button>
      </div>
      {/* Content */}
      <div className="mb-3">
        {post?.title && <h3 className="font-heading font-semibold text-base mb-1" style={{ color: 'var(--color-foreground)' }}>{post?.title}</h3>}
        <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--color-foreground)' }}>{post?.content}</p>
      </div>
      {/* Poll */}
      {post?.type === 'poll' && post?.pollOptions &&
      <div className="mb-3 space-y-2">
          {post?.pollOptions?.map((opt, i) =>
        <button key={i} className="w-full text-left px-3 py-2 rounded-lg border text-sm font-caption transition-all hover:border-amber-400"
        style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}>
              <div className="flex justify-between items-center mb-1">
                <span>{opt?.label}</span>
                <span className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{opt?.pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${opt?.pct}%`, background: 'var(--color-accent)' }} />
              </div>
            </button>
        )}
          <p className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{post?.pollVotes} votes · {post?.pollEnds}</p>
        </div>
      }
      {/* Image */}
      {post?.image &&
      <div className="mb-3 rounded-lg overflow-hidden h-48 md:h-56">
          <Image src={post?.image} alt={post?.imageAlt} className="w-full h-full object-cover" />
        </div>
      }
      {/* Tags */}
      {post?.tags && post?.tags?.length > 0 &&
      <div className="flex flex-wrap gap-1.5 mb-3">
          {post?.tags?.map((tag) =>
        <span key={tag} className="px-2 py-0.5 rounded-full text-xs font-caption cursor-pointer hover:opacity-80 transition-opacity"
        style={{ background: 'rgba(27,42,94,0.08)', color: 'var(--color-primary)' }}>
              #{tag}
            </span>
        )}
        </div>
      }
      {/* Stats */}
      {totalReactions > 0 &&
      <div className="flex items-center gap-1 mb-2 pb-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex -space-x-1">
            {REACTIONS?.filter((r) => reactions?.[r?.id] > 0)?.slice(0, 3)?.map((r) =>
          <span key={r?.id} className="w-5 h-5 rounded-full flex items-center justify-center text-xs" style={{ background: 'var(--color-muted)' }}>
                <Icon name={r?.icon} size={11} color={r?.color} />
              </span>
          )}
          </div>
          <span className="text-xs font-caption ml-1" style={{ color: 'var(--color-muted-foreground)' }}>{totalReactions}</span>
          <span className="ml-auto text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{post?.comments} comments</span>
        </div>
      }
      {/* Actions */}
      <div className="flex items-center gap-1 relative">
        <div className="relative">
          <button
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-caption transition-all hover:bg-gray-50 min-h-[44px] ${userReaction ? 'font-semibold' : ''}`}
            style={{ color: userReaction ? 'var(--color-accent)' : 'var(--color-muted-foreground)' }}
            onMouseEnter={() => setShowReactionPicker(true)}
            onMouseLeave={() => setShowReactionPicker(false)}
            onClick={() => !userReaction && handleReaction('coin')}>
            
            <Icon name={userReaction ? REACTIONS?.find((r) => r?.id === userReaction)?.icon || 'Coins' : 'Coins'} size={16} color={userReaction ? 'var(--color-accent)' : 'currentColor'} />
            <span className="hidden sm:inline">React</span>
          </button>
          {showReactionPicker &&
          <div
            className="absolute bottom-full left-0 mb-1 flex items-center gap-1 p-2 rounded-xl shadow-xl border bg-white z-10"
            style={{ borderColor: 'var(--color-border)' }}
            onMouseEnter={() => setShowReactionPicker(true)}
            onMouseLeave={() => setShowReactionPicker(false)}>
            
              {REACTIONS?.map((r) =>
            <button key={r?.id} onClick={() => handleReaction(r?.id)}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-125 ${userReaction === r?.id ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
            title={r?.label}>
                  <Icon name={r?.icon} size={18} color={r?.color} />
                </button>
            )}
            </div>
          }
        </div>

        <button
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-caption transition-all hover:bg-gray-50 min-h-[44px]"
          style={{ color: 'var(--color-muted-foreground)' }}
          onClick={() => setShowComments(!showComments)}>
          
          <Icon name="MessageCircle" size={16} color="currentColor" />
          <span className="hidden sm:inline">Comment</span>
        </button>

        <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-caption transition-all hover:bg-gray-50 min-h-[44px]"
        style={{ color: 'var(--color-muted-foreground)' }}>
          <Icon name="Share2" size={16} color="currentColor" />
          <span className="hidden sm:inline">Share</span>
        </button>

        <button className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-caption transition-all hover:bg-gray-50 min-h-[44px]"
        style={{ color: 'var(--color-muted-foreground)' }}>
          <Icon name="Bookmark" size={16} color="currentColor" />
        </button>
      </div>
      {/* Comment Input */}
      {showComments &&
      <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex gap-2">
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gray-200">
              <Image src="https://images.unsplash.com/photo-1669553640543-863be898aee4" alt="Current user profile photo of woman with dark hair" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 flex gap-2">
              <input
              value={commentText}
              onChange={(e) => setCommentText(e?.target?.value)}
              placeholder="Add a comment..."
              className="flex-1 px-3 py-2 rounded-lg border text-sm font-caption outline-none focus:ring-2"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)', background: 'var(--color-surface)', '--tw-ring-color': 'var(--color-accent)' }} />
            
              <button className="px-3 py-2 rounded-lg text-sm font-caption font-medium transition-all"
            style={{ background: 'var(--color-accent)', color: 'var(--color-primary)' }}>
                Post
              </button>
            </div>
          </div>
        </div>
      }
    </article>);

}